import styled, { keyframes } from 'styled-components';

// Backdrop overlay
export const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)'};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

// Modal slide-in animation
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Modal container
export const ModalContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: ${(props) => props.maxWidth || '540px'};
  max-height: 90vh;
  border-radius: 12px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 20px 60px;
  animation: ${slideIn} 0.3s ease-out;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(30, 41, 59, 1)'
      : 'rgba(255, 255, 255, 1)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 95vh;
  }
`;

// Close button
export const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
    color: ${(props) =>
      props.theme.name === 'dark'
        ? props.theme.colors?.text?.primary || '#ffffff'
        : '#334155'};
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors?.primary || '#FA4616'};
    outline-offset: 2px;
  }
`;

// Form wrapper with flex layout
export const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
`;

// Modal header
export const ModalHeader = styled.div`
  padding: 32px 32px 0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 24px 24px 0;
  }
`;

// Title with accent bar
export const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 8px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 12px;
  padding-right: 40px;

  /* Accent bar */
  &::before {
    content: '';
    width: 4px;
    height: 28px;
    background: ${(props) => props.theme.colors?.primary || '#FA4616'};
    border-radius: 2px;
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    font-size: 20px;

    &::before {
      height: 24px;
    }
  }
`;

// Subtitle/description
export const ModalSubtitle = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 24px;
  padding-left: 16px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};
`;

// Modal content (scrollable)
export const ModalContent = styled.div`
  padding: 24px 32px;
  overflow-y: auto;
  flex: 1;

  @media (max-width: 768px) {
    padding: 20px 24px;
  }
`;

// Form group
export const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

// Form label
export const FormLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
`;

// Form input
export const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.15s ease;
  box-sizing: border-box;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors?.primary || '#FA4616'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark'
        ? `0 0 0 1px ${props.theme.colors?.primary || '#FA4616'}`
        : '0 0 0 3px rgba(250,70,22,0.1)'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Form textarea
export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.15s ease;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 1)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.15)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &::placeholder {
    color: ${(props) => (props.theme.name === 'dark' ? '#64748b' : '#94a3b8')};
  }

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors?.primary || '#FA4616'};
    box-shadow: ${(props) =>
      props.theme.name === 'dark'
        ? `0 0 0 1px ${props.theme.colors?.primary || '#FA4616'}`
        : '0 0 0 3px rgba(250,70,22,0.1)'};
  }
`;

// Toggle row (for toggle switches)
export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 12px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.06)'};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ToggleInfo = styled.div`
  flex: 1;
  margin-right: 16px;
`;

export const ToggleTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin-bottom: 4px;
`;

export const ToggleDescription = styled.div`
  font-size: 13px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};
`;

// Error message
export const ErrorMessage = styled.span`
  display: block;
  color: ${(props) => props.theme.colors?.error || '#ED4337'};
  font-size: 12px;
  margin-top: 6px;
`;

// Modal footer
export const ModalFooter = styled.div`
  padding: 24px 32px;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  flex-shrink: 0;
  border-top: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.08)'};

  @media (max-width: 768px) {
    padding: 20px 24px;
    flex-direction: column-reverse;
  }
`;

// Primary button
export const PrimaryButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  background: ${(props) => props.theme.colors?.primary || '#FA4616'};
  min-width: 120px;

  &:hover:not(:disabled) {
    background: #e03e0c;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(250, 70, 22, 0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors?.primary || '#FA4616'};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Secondary button
export const SecondaryButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
  background: transparent;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.15)'};
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.05)'};
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.colors?.primary || '#FA4616'};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

// Divider
export const Divider = styled.div`
  height: 1px;
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.08)'};
  margin: 24px 0;
`;

// Section title within form
export const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 16px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.text?.secondary || '#94A3B8'
      : '#64748b'};
`;
